import React, { useCallback, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, CheckCircle2, ChevronDown, ChevronRight, Info, RotateCw, Search, Trash2, TriangleAlert, X } from 'lucide-react';
import CodeEditor from './CodeEditor';
import CompactSelectPicker from './CompactSelectPicker';
import Tooltip from './Tooltip';
import { LOG_FILTERS, filterEntries, detectPayloadLanguage, formatPayload, formatLogTime, oneLine } from '../../utils/streamLog';
import './StreamLogTable.css';

const FILTER_LABEL = { all: 'All messages', received: 'Received', sent: 'Sent', events: 'Connection' };
const LANGUAGES = [
  { value: 'text', label: 'Text' }, { value: 'json', label: 'JSON' },
  { value: 'xml', label: 'XML' }, { value: 'html', label: 'HTML' },
];
const MONACO_LANGUAGE = { text: 'plaintext', json: 'json', xml: 'xml', html: 'html' };

function RowIcon({ entry }) {
  if (entry.kind === 'event') {
    if (entry.event === 'connected') return <CheckCircle2 size={14} className="stream-log__icon stream-log__icon--ok" aria-label="Connected" />;
    if (entry.event === 'reconnecting') return <RotateCw size={13} className="stream-log__icon" aria-label="Reconnecting" />;
    if (entry.event === 'error') return <TriangleAlert size={14} className="stream-log__icon stream-log__icon--error" aria-label="Error" />;
    return <Info size={14} className="stream-log__icon" aria-label="Disconnected" />;
  }
  return entry.direction === 'out'
    ? <ArrowUp size={13} className="stream-log__icon stream-log__icon--out" aria-label="Sent" />
    : <ArrowDown size={13} className="stream-log__icon stream-log__icon--in" aria-label="Received" />;
}

/** The handshake as it really went (desktop client only): one block per direction. */
function HeaderList({ title, headers }) {
  if (!headers?.length) return null;
  return (
    <div className="stream-log__headers">
      <div className="stream-log__headers-title">{title}</div>
      <dl>
        {headers.map(([name, value], i) => (
          // eslint-disable-next-line react/no-array-index-key -- a header name may repeat; the list never reorders
          <React.Fragment key={`${name}-${i}`}><dt>{name}</dt><dd>{value}</dd></React.Fragment>
        ))}
      </dl>
    </div>
  );
}

/** What a connection row knows: the close code and reason, the URL — never invented handshake data. */
function EventDetail({ entry, handshakeNote }) {
  const { detail } = entry;
  const close = detail?.close;
  return (
    <div className="stream-log__detail stream-log__detail--event">
      {close && (
        <p><strong>{close.code} {close.name}</strong>{close.reason ? `: ${close.reason}` : ''}</p>
      )}
      {detail?.message && <p>{detail.message}</p>}
      {detail?.url && <p><span className="stream-log__muted">URL</span> <code>{detail.url}</code></p>}
      {detail?.handshake && (
        <>
          <p><span className="stream-log__muted">Handshake</span> <strong>{detail.handshake.status} {detail.handshake.status === 101 ? 'Switching Protocols' : ''}</strong></p>
          <HeaderList title="Request headers" headers={detail.handshake.requestHeaders} />
          <HeaderList title="Response headers" headers={detail.handshake.responseHeaders} />
        </>
      )}
      {entry.event === 'connected' && !detail?.handshake && handshakeNote && <p className="stream-log__muted">{handshakeNote}</p>}
    </div>
  );
}

function MessageDetail({ entry }) {
  const [language, setLanguage] = useState(() => detectPayloadLanguage(entry.data));
  const text = useMemo(() => formatPayload(entry.data, language), [entry.data, language]);
  const lines = Math.min(Math.max(text.split('\n').length, 2), 16);
  return (
    <div className="stream-log__detail">
      <div className="stream-log__detail-bar">
        <CompactSelectPicker value={language} onChange={setLanguage} options={LANGUAGES} aria-label="Show as" className="stream-log__picker" />
      </div>
      {/* CodeEditor fills its parent — the parent is what has a height */}
      <div className="stream-log__preview" style={{ height: lines * 17 + 10 }}>
        <CodeEditor hideChrome readOnly value={text} onChange={() => {}} language={MONACO_LANGUAGE[language]} height="100%" />
      </div>
    </div>
  );
}

/** One row. Memoized: a busy feed adds a row a moment — the other thousand must not render again. */
const LogRow = React.memo(function LogRow({ entry, open, onToggle }) {
  return (
    <div
      role="row"
      tabIndex={0}
      aria-expanded={open}
      className={`req-kv-row req-kv-row--log stream-log__row${open ? ' stream-log__row--open' : ''}${entry.kind === 'event' ? ' stream-log__row--event' : ''}`}
      onClick={() => onToggle(entry.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(entry.id); } }}
    >
      <div className="req-kv-cell stream-log__cell stream-log__cell--icon" role="cell"><RowIcon entry={entry} /></div>
      <div className="req-kv-cell stream-log__cell stream-log__cell--data" role="cell">
        {entry.tag && <span className="stream-log__tag">{entry.tag}</span>}
        <span className="stream-log__text">{oneLine(entry.data) || '(empty)'}</span>
      </div>
      <div className="req-kv-cell stream-log__cell stream-log__cell--time" role="cell">{formatLogTime(entry.timestamp)}</div>
      <div className="req-kv-cell stream-log__cell stream-log__cell--chevron" role="cell">
        {open ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
      </div>
    </div>
  );
});

/**
 * The log of a live connection as a TABLE — the HTTP builder's key/value table look
 * (`.req-kv-table` borders and row height): direction · payload · time, newest first.
 * Connection events are rows too. A click opens the row: a highlighted preview with a
 * "show as" picker for a message, the close code / reason for a connection row.
 * Shared by the WebSocket and the SSE builder (the SSE event name is the row's `tag`).
 *
 * @param {{ entries: object[], dropped?: number, emptyText: string, onClear?: () => void,
 *           filters?: string[], handshakeNote?: string }} props
 */
export default function StreamLogTable({ entries, dropped = 0, emptyText, onClear, filters = LOG_FILTERS, handshakeNote = '' }) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState(null);

  const toggle = useCallback((id) => setOpenId((current) => (current === id ? null : id)), []);
  // An opened row near the bottom would open out of sight. ONE stable callback: React calls a
  // ref when the node mounts — an inline function would run on every render (= every new message).
  const revealDetail = useCallback((node) => {
    if (node?.scrollIntoView) requestAnimationFrame(() => node.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  }, []);

  const rows = useMemo(() => [...filterEntries(entries, { filter, query })].reverse(), [entries, filter, query]);

  return (
    <div className="stream-log">
      <div className="stream-log__bar">
        <div className="stream-log__search">
          <Search size={13} aria-hidden />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search" aria-label="Search the log" spellCheck={false} />
          {query && (
            <button type="button" className="stream-log__search-clear" onClick={() => setQuery('')} aria-label="Clear search"><X size={12} /></button>
          )}
        </div>
        {filters.length > 1 && (
          <CompactSelectPicker
            value={filter}
            onChange={setFilter}
            options={filters.map((f) => ({ value: f, label: FILTER_LABEL[f] }))}
            aria-label="Filter the log"
            className="stream-log__picker"
          />
        )}
        <span className="stream-log__count" aria-live="polite">{rows.length}{rows.length !== entries.length ? ` / ${entries.length}` : ''}</span>
        {onClear && entries.length > 0 && (
          <Tooltip content="Clear the log" side="bottom">
            <button type="button" className="stream-log__clear" onClick={() => { setOpenId(null); onClear(); }} aria-label="Clear the log">
              <Trash2 size={13} aria-hidden />
            </button>
          </Tooltip>
        )}
      </div>

      <div className="stream-log__body panel-scroll">
        {entries.length === 0 ? (
          <div className="stream-log__empty">{emptyText}</div>
        ) : rows.length === 0 ? (
          <div className="stream-log__empty">Nothing matches.</div>
        ) : (
          <div className="req-kv-table stream-log__table" role="table" aria-label="Log">
            {rows.map((entry) => {
              const open = openId === entry.id;
              return (
                <React.Fragment key={entry.id}>
                  <LogRow entry={entry} open={open} onToggle={toggle} />
                  {open && (
                    <div ref={revealDetail}>
                      {entry.kind === 'event'
                        ? <EventDetail entry={entry} handshakeNote={handshakeNote} />
                        : <MessageDetail entry={entry} />}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
        {dropped > 0 && <div className="stream-log__dropped">{dropped} older {dropped === 1 ? 'entry was' : 'entries were'} dropped to keep the log bounded.</div>}
      </div>
    </div>
  );
}
